import { useEffect, useState, type RefObject } from 'react';

export interface ProgressiveOptions {
  /** Items rendered before any scrolling. */
  initial?: number;
  /** Items added each time the end comes near. */
  step?: number;
  /** How far below the visible area (px) the end counts as near, so the next rows are ready before you reach them. */
  margin?: number;
}

/**
 * How many items of a long list to render: a first screenful, then more as
 * the end of what is rendered comes within `margin` of the scroller's bottom.
 * Put the returned `sentinelRef` on an element after the last item.
 *
 * Starts over at `initial` whenever `resetKey` changes (a new search or
 * filter), but not when the list itself changes, so a refetch after a delete
 * keeps what you have scrolled through. Without IntersectionObserver
 * (old browsers, jsdom) everything renders.
 */
export function useProgressiveCount(
  total: number,
  resetKey: string,
  rootRef: RefObject<HTMLElement | null>,
  { initial = 36, step = 36, margin = 2000 }: ProgressiveOptions = {},
): { count: number; hasMore: boolean; sentinelRef: (el: HTMLElement | null) => void } {
  const supported = typeof IntersectionObserver !== 'undefined';
  const [paging, setPaging] = useState({ key: resetKey, count: initial });
  // A new key means a new list: derive the reset instead of syncing it in an effect
  const target = paging.key === resetKey ? paging.count : initial;
  const count = supported ? Math.min(total, target) : total;
  const hasMore = count < total;
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);

  // Recreated after every step: observing fires once with the current state,
  // so if the end is still near (short cards, tall window) the next step follows
  useEffect(() => {
    if (!supported || !sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        setPaging((prev) => ({ key: resetKey, count: (prev.key === resetKey ? prev.count : initial) + step }));
      },
      { root: rootRef.current, rootMargin: `0px 0px ${margin}px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [supported, sentinel, hasMore, count, resetKey, initial, step, margin, rootRef]);

  return { count, hasMore, sentinelRef: setSentinel };
}
